'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  driversApi,
  driverKeys,
  type Driver,
  type DriversListParams
} from '@/features/drivers';
import {
  clientsApi,
  clientKeys,
  type Client,
  type ClientsListParams
} from '@/features/clients';
import {
  usersApi,
  userKeys,
  type User,
  type UsersListParams
} from '@/features/users';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import {
  AlertCircle,
  Camera,
  Loader2,
  Package,
  ScanLine,
  Trash2
} from 'lucide-react';
import { QrScanDialog } from '@/components/qr-scan-dialog';
import { Link } from '@/i18n/routing';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useQrNavigateSearch } from '@/hooks/use-qr-navigate-search';
import { SelectAllBanner } from '@/components/select-all-banner';
import { RECEPTION_HASH } from '../constants';
import { receptionApi } from '../services/reception.api';
import { receptionQueries } from '../services/reception.queries';
import { useBulkDeleteShipmentsMutation } from '../services/reception.mutations';
import { ReceptionTableActionBar } from './reception-table-action-bar';
import { ReceptionCard } from './reception-card';
import { ReceptionCompactRow } from './reception-compact-row';
import type { Shipment, ShipmentsListParams } from '../types';

type ReceptionStatus = 'matched' | 'processing' | 'not_matched' | 'pending';

const RECEPTION_STATUS_STYLES: Record<
  ReceptionStatus,
  { dot: string; badge: string }
> = {
  matched: {
    dot: 'bg-green-500',
    badge:
      'bg-green-50 text-green-700 hover:bg-green-50 dark:bg-green-950 dark:text-green-300'
  },
  processing: {
    dot: 'bg-amber-500',
    badge:
      'bg-amber-50 text-amber-700 hover:bg-amber-50 dark:bg-amber-950 dark:text-amber-300'
  },
  not_matched: {
    dot: 'bg-red-500',
    badge:
      'bg-red-50 text-red-700 hover:bg-red-50 dark:bg-red-950 dark:text-red-300'
  },
  pending: {
    dot: 'bg-gray-400',
    badge:
      'bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
  }
};

function getReceptionStatus(shipment: Shipment): ReceptionStatus {
  if (shipment.active_session) return 'processing';
  if (shipment.latest_session?.status === 'completed') {
    return shipment.latest_session.count_status === 'mismatched'
      ? 'not_matched'
      : 'matched';
  }
  return 'pending';
}

export function ReceptionListing() {
  const t = useTranslations('reception');
  const tc = useTranslations('common');
  const router = useRouter();
  // Overlay open-state lives in the URL hash (#delete/<id>, #scan) so overlays
  // are deep-linkable and close on browser Back. Destructure the stable
  // callbacks so they can go in hook dep arrays without tripping the React
  // Compiler's manual-memoization check.
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(RECEPTION_HASH.delete);
  const [scannerActive, setScannerActive] = useState(true);
  const { open: scanDialogOpen, onOpenChange: toggleScanDialog } =
    useHashDialog(RECEPTION_HASH.scan);
  const [hwScan, setHwScan] = useState<{ value: string; key: number } | null>(
    null
  );
  const [view, setView] = useDataView('reception');

  const receptionStatusLabels: Record<ReceptionStatus, string> = {
    matched: t('metricMatched'),
    processing: t('metricProcessing'),
    not_matched: t('metricNotMatched'),
    pending: t('statusPending')
  };

  const columns: ColumnDef<Shipment>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'shipment_number',
      accessorKey: 'shipment_number',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('shipmentNumber')} />
      ),
      cell: ({ row, column }) => {
        const isUrgent = row.original.priority === 'urgent';
        return (
          <div className='flex items-center gap-2'>
            {isUrgent && (
              <AlertCircle className='h-4 w-4 shrink-0 animate-pulse text-red-500' />
            )}
            <Link
              href={`/dashboard/shipment-statement/${row.original.id}`}
              className={cn(
                'font-medium hover:underline',
                isUrgent ? 'text-red-600 dark:text-red-400' : 'text-primary'
              )}
            >
              <HighlightMatch
                text={row.original.shipment_number}
                query={getColumnSearchTerms(column)}
              />
            </Link>
          </div>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: t('searchPlaceholder'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'reception.column.shipmentNumber'
      }
    },
    {
      id: 'driver',
      accessorFn: (row) => row.driver?.name ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('driver')} />
      ),
      cell: ({ row }) => (
        <span className='font-medium'>{row.original.driver?.name ?? '—'}</span>
      ),
      enableColumnFilter: true,
      meta: {
        label: t('driver'),
        variant: 'infiniteMultiSelect' as const
      }
    },
    {
      id: 'box_count',
      accessorKey: 'box_count',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('boxCount')} />
      ),
      cell: ({ row }) => <span>{row.original.box_count.toLocaleString()}</span>
    },
    {
      id: 'shipment_date',
      accessorKey: 'shipment_date',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('shipmentDate')} />
      ),
      cell: ({ row }) => {
        const date = row.original.shipment_date;
        if (!date) return <span className='text-muted-foreground'>-</span>;
        return (
          <span>
            {new Date(date).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: t('shipmentDate'),
        variant: 'dateRange' as const
      }
    },
    {
      id: 'status',
      accessorFn: (row) => getReceptionStatus(row),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => {
        const status = getReceptionStatus(row.original);
        const styles = RECEPTION_STATUS_STYLES[status];
        return (
          <Badge variant='secondary' className={cn('gap-1.5', styles.badge)}>
            <span className={cn('h-2 w-2 rounded-full', styles.dot)} />
            {receptionStatusLabels[status]}
          </Badge>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('filterStatus'),
        variant: 'multiSelect' as const,
        icon: 'solar:tag-bold-duotone',
        options: [
          { label: t('statusPending'), value: 'pending' },
          { label: t('metricMatched'), value: 'matched' },
          { label: t('metricProcessing'), value: 'processing' },
          { label: t('metricNotMatched'), value: 'not_matched' }
        ],
        permission: 'reception.column.status'
      }
    },
    {
      id: 'priority',
      accessorKey: 'priority',
      header: () => null,
      cell: () => null,
      size: 0,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: true,
      meta: {
        label: t('priority'),
        variant: 'multiSelect' as const,
        icon: 'solar:flag-bold-duotone',
        options: [
          { value: 'normal', label: t('priorityNormal') },
          { value: 'high', label: t('priorityHigh') },
          { value: 'urgent', label: t('priorityUrgent') }
        ]
      }
    },
    // {
    //   id: 'client_name',
    //   accessorFn: (row) => row.judicial_entity,
    //   header: () => null,
    //   cell: () => null,
    //   size: 0,
    //   enableSorting: false,
    //   enableHiding: false,
    //   enableColumnFilter: true,
    //   meta: {
    //     label: t('filterClient'),
    //     variant: 'infiniteMultiSelect' as const
    //   }
    // },
    // {
    //   id: 'created_by',
    //   accessorFn: (row) => row.created_by,
    //   header: () => null,
    //   cell: () => null,
    //   size: 0,
    //   enableSorting: false,
    //   enableHiding: false,
    //   enableColumnFilter: true,
    //   meta: {
    //     label: t('filterCreator'),
    //     variant: 'infiniteMultiSelect' as const
    //   }
    // },
    // {
    //   id: 'location',
    //   accessorFn: (row) => row.location,
    //   header: () => null,
    //   cell: () => null,
    //   size: 0,
    //   enableSorting: false,
    //   enableHiding: false,
    //   enableColumnFilter: true,
    //   meta: {
    //     label: t('filterLocation'),
    //     variant: 'infiniteMultiSelect' as const
    //   }
    // },
    // {
    //   id: 'branch',
    //   accessorFn: (row) => row.branch,
    //   header: () => null,
    //   cell: () => null,
    //   size: 0,
    //   enableSorting: false,
    //   enableHiding: false,
    //   enableColumnFilter: true,
    //   meta: {
    //     label: t('filterBranch'),
    //     variant: 'infiniteMultiSelect' as const
    //   }
    // },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isInProgress = !!item.active_session;
        return (
          <div className='flex items-center justify-end gap-1'>
            {item.active_session ? (
              <Button
                size='sm'
                onClick={() =>
                  router.push(
                    `/dashboard/shipment-statement/${item.id}/continue`
                  )
                }
              >
                {t('followUp')}
              </Button>
            ) : item.latest_session?.status === 'completed' ? (
              <>
                <Badge className='bg-green-500 text-white'>
                  {t('completed')}
                </Badge>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    router.push(`/dashboard/shipment-statement/${item.id}`)
                  }
                >
                  {t('view')}
                </Button>
              </>
            ) : (
              <Button
                size='sm'
                onClick={() =>
                  router.push(
                    `/dashboard/shipment-statement/${item.id}/continue`
                  )
                }
              >
                {t('initialize')}
              </Button>
            )}
            <Button
              variant='ghost'
              size='sm'
              disabled={isInProgress}
              onClick={() => openDelete(String(item.id))}
              className='text-muted-foreground hover:text-destructive'
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const mapDriverOption = useCallback(
    (d: Driver) => ({ value: String(d.id), label: d.name }),
    []
  );
  const driverInfinite = useInfiniteSelect<Driver, DriversListParams>({
    queryKey: driverKeys.lists(),
    queryFn: (params) => driversApi.getAll(params),
    mapOption: mapDriverOption
  });

  const mapClientOption = useCallback(
    (c: Client) => ({ value: c.name, label: c.name }),
    []
  );
  const clientInfinite = useInfiniteSelect<Client>({
    queryKey: clientKeys.lists(),
    queryFn: (params) => clientsApi.getAll(params as ClientsListParams),
    mapOption: mapClientOption
  });

  const mapCreatorOption = useCallback(
    (u: User) => ({ value: String(u.id), label: u.name }),
    []
  );
  const creatorInfinite = useInfiniteSelect<User>({
    queryKey: userKeys.lists(),
    queryFn: (params) => usersApi.getAll(params as UsersListParams),
    mapOption: mapCreatorOption
  });

  const mapLocationOption = useCallback(
    (item: { value: string }) => ({ value: item.value, label: item.value }),
    []
  );
  const locationInfinite = useInfiniteSelect<
    { value: string },
    { field: string; page?: number; per_page?: number; search?: string }
  >({
    queryKey: ['reception', 'filter-options', 'location'],
    queryFn: (params) =>
      receptionApi.filterOptions({ ...params, field: 'location' }),
    mapOption: mapLocationOption
  });

  const mapBranchOption = useCallback(
    (item: { value: string }) => ({ value: item.value, label: item.value }),
    []
  );
  const branchInfinite = useInfiniteSelect<
    { value: string },
    { field: string; page?: number; per_page?: number; search?: string }
  >({
    queryKey: ['reception', 'filter-options', 'branch'],
    queryFn: (params) =>
      receptionApi.filterOptions({ ...params, field: 'branch' }),
    mapOption: mapBranchOption
  });

  // URL state
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'shipment_number',
    parseAsString.withDefault('')
  );
  const [urlDriver] = useQueryState(
    'driver',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlPriority] = useQueryState(
    'priority',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Shipment>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Shipment>[])
  );
  const [urlClientName] = useQueryState(
    'client_name',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCreatedBy] = useQueryState(
    'created_by',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlLocation] = useQueryState(
    'location',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlBranch] = useQueryState(
    'branch',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlShipmentDate] = useQueryState(
    'shipment_date',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const queryParams = useMemo<ShipmentsListParams>(() => {
    const params: ShipmentsListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlDriver.length) {
      params.driver_id = urlDriver.map(Number).filter((n) => n > 0);
    }
    if (urlPriority.length) {
      params.priority = urlPriority as Array<'normal' | 'high' | 'urgent'>;
    }
    if (urlStatus.length) {
      params.status = urlStatus;
    }
    if (urlClientName.length) params.client_name = urlClientName;
    if (urlCreatedBy.length)
      params.created_by = urlCreatedBy.map(Number).filter((n) => n > 0);
    if (urlLocation.length) params.location = urlLocation;
    if (urlBranch.length) params.branch = urlBranch;
    if (urlShipmentDate.length) {
      const t0 = Number(urlShipmentDate[0]);
      const t1 = Number(urlShipmentDate[1]);
      if (t0) params.date_from = new Date(t0).toISOString().split('T')[0];
      if (t1) params.date_to = new Date(t1).toISOString().split('T')[0];
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlDriver,
    urlPriority,
    urlStatus,
    urlSorting,
    urlClientName,
    urlCreatedBy,
    urlLocation,
    urlBranch,
    urlShipmentDate
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    receptionQueries.list(queryParams)
  );
  const bulkDeleteMutation = useBulkDeleteShipmentsMutation();

  const shipments = (data?.data ?? []) as Shipment[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const deleteTarget = deleteOpen
    ? (shipments.find((s) => String(s.id) === deleteId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Shipment>({
    columns: filteredColumns,
    pageKey: 'reception',
    data: shipments,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const handleDeleteTarget = useCallback(
    (shipment: Shipment) => {
      openDelete(String(shipment.id));
    },
    [openDelete]
  );

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('shipmentNumber'), value: 'shipment_number' },
      { label: t('boxCount'), value: 'box_count' },
      { label: t('shipmentDate'), value: 'shipment_date' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await bulkDeleteMutation.mutateAsync({ ids: [deleteTarget.id] });
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  const queryClient = useQueryClient();

  const handleScanNavigate = useCallback(
    async (id: number) => {
      try {
        const response = await queryClient.fetchQuery(
          receptionQueries.detail(id)
        );
        const shipment = response.data;
        if (shipment.active_session) {
          router.push(`/dashboard/shipment-statement/${id}/continue`);
        } else if (shipment.latest_session?.status === 'completed') {
          router.push(`/dashboard/shipment-statement/${id}`);
        } else {
          router.push(`/dashboard/shipment-statement/${id}/continue`);
        }
      } catch {
        router.push(`/dashboard/shipment-statement/${id}`);
      }
    },
    [queryClient, router]
  );

  const { results: hwScanResults, isFetching: hwScanFetching } =
    useQrNavigateSearch({
      searchInput: hwScan?.value ?? '',
      stage: 'reception',
      enabled: !!hwScan
    });

  useEffect(() => {
    if (!hwScan || hwScanFetching) return;
    if (hwScanResults.length > 0) {
      handleScanNavigate(hwScanResults[0].id);
    } else {
      toast.error(t('notFound'));
    }
  }, [hwScanResults, hwScanFetching, hwScan, handleScanNavigate, t]);

  useHardwareScanner({
    onScan: (value) => setHwScan({ value, key: Date.now() }),
    enabled: scannerActive && !scanDialogOpen
  });

  return (
    <div className='space-y-4'>
      {/* Scan QR button row + view toggle */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          {IS_QR_SCANNER_ENABLED && (
            <>
              <Button
                onClick={() => {
                  setScannerActive((prev) => {
                    if (!prev) (document.activeElement as HTMLElement)?.blur();
                    return !prev;
                  });
                }}
              >
                <span className='relative me-2 flex h-2 w-2 shrink-0'>
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full rounded-full opacity-75',
                      scannerActive
                        ? 'animate-ping bg-green-400'
                        : 'bg-gray-400'
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-2 w-2 rounded-full',
                      scannerActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                  />
                </span>
                <ScanLine className='me-2 h-4 w-4' />
                {t('scanQr')}
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={() => toggleScanDialog(true)}
                title={tc('scanCamera')}
              >
                <Camera className='h-4 w-4' />
              </Button>
            </>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='reception'
          />
        </div>
      </div>

      <DataTableToolbar
        table={table}
        infiniteConfigs={{
          driver: driverInfinite
          // client_name: clientInfinite,
          // created_by: creatorInfinite,
          // location: locationInfinite,
          // branch: branchInfinite
        }}
        viewFilterPermission='reception.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : shipments.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Package className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noShipments')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {shipments.map((shipment) => (
              <ReceptionCard
                key={shipment.id}
                shipment={shipment}
                onDelete={handleDeleteTarget}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {shipments.map((shipment) => (
              <ReceptionCompactRow
                key={shipment.id}
                shipment={shipment}
                onDelete={handleDeleteTarget}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={shipments.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <ReceptionTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                number: deleteTarget?.shipment_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QrScanDialog
        open={scanDialogOpen}
        onOpenChange={toggleScanDialog}
        stage='reception'
        onNavigate={handleScanNavigate}
        translationNamespace='reception'
      />
    </div>
  );
}
